import React, { useState, useEffect } from 'react';
import {
  Building2,
  Mail,
  Settings,
  Save,
  TestTube,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Phone,
  MapPin,
  Loader2
} from 'lucide-react';
import { companyService } from '../../services/companyService';
import { emailSettingsService } from '../../services/emailSettingsService';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('company');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [testEmailResult, setTestEmailResult] = useState(null);

  // Company Info State
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    street: '',
    number: '',
    block: '',
    staircase: '',
    floor: '',
    apartment: '',
    county: '',
    commercialRegister: '',
    fiscalCode: '',
    phone: '',
    representativeName: '',
    representativeEmail: '',
    representativePosition: '',
    iban: '',
    bank: ''
  });

  // Email Settings State
  const [emailSettings, setEmailSettings] = useState({
    smtpServer: '',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    fromEmail: '',
    fromName: '',
    enableSsl: true
  });

  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { success: true/false, message: '' }

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);

    try {
      const response = await emailSettingsService.testConnection(emailSettings);
      setTestResult({ success: true, message: response.data.message });
    } catch (error) {
      // Presupunem că backend-ul returnează 409 Conflict în caz de eșec
      const errorMessage = error.response?.data?.message || 'Eroare necunoscută la testare.';
      setTestResult({ success: false, message: errorMessage });
    } finally {
      setTesting(false);
    }
  };
  // System Settings State
  const [systemSettings, setSystemSettings] = useState({
    defaultContractExpiryMonth: 12,
    defaultContractExpiryDay: 31,
    autoRenewalEnabled: true,
    emailReminderDaysBefore: [30, 15, 7, 1],
    accessTokenExpiryHours: 168,
    contractNumberPrefix: 'CNT',
    additionalActPrefix: 'AA'
  });

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setInitialLoading(true);
    setError(null);

    try {
      // Load company info for current user
      try {
        const response = await companyService.getCompanyInfo();
        if (response.data) {
          setCompanyInfo(response.data);
        }
      } catch (err) {
        // If 404, user doesn't have company info yet - that's okay
        if (err.response?.status !== 404) {
          console.error('Error loading company info:', err);
        }
      }

      try {
        const response = await emailSettingsService.getSettings();
        // Dacă primești date, le pui în state
        if (response.data) {
          setEmailSettings(response.data);
        }
      } catch (error) {
        console.error("Failed to load email settings", error);
      }

    } catch (error) {
      console.error('Error loading settings:', error);
      setError('Eroare la încărcarea setărilor');
    } finally {
      setInitialLoading(false);
    }
  };

  const saveCompanyInfo = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await companyService.createOrUpdateCompanyInfo(companyInfo);

      if (response.data) {
        setCompanyInfo(response.data);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Error saving company info:', error);
      setError(error.response?.data?.message || 'Eroare la salvarea informațiilor companiei');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEmailSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await emailSettingsService.updateSettings(emailSettings);
      setMessage({ type: 'success', text: 'Setările SMTP au fost salvate cu succes!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Eroare la salvarea setărilor.' });
    } finally {
      setSaving(false);
    }
  };

  const saveEmailSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/settings/email', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(emailSettings)
      });

      if (response.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Error saving email settings:', error);
      setError('Eroare la salvarea setărilor email');
    } finally {
      setLoading(false);
    }
  };

  const saveSystemSettings = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/settings/system', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(systemSettings)
      });

      if (response.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      }
    } catch (error) {
      console.error('Error saving system settings:', error);
      setError('Eroare la salvarea setărilor sistem');
    } finally {
      setLoading(false);
    }
  };

  const testEmailConnection = async () => {
    setLoading(true);
    setTestEmailResult(null);
    try {
      const response = await fetch('/api/email/test', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(emailSettings)
      });

      const result = await response.json();
      setTestEmailResult(result.success ? 'success' : 'error');
    } catch (error) {
      setTestEmailResult('error');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'company', label: 'Informații Firmă', icon: Building2 },
    { id: 'email', label: 'Setări Email', icon: Mail },
    { id: 'system', label: 'Setări Sistem', icon: Settings }
  ];

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Se încarcă setările...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Setări</h1>
          <p className="text-gray-600">Configurați informațiile companiei și setările sistemului</p>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
            <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
            <span className="text-green-700">Setările au fost salvate cu succes!</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <XCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
            <span className="text-red-700">{error}</span>
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex space-x-4 md:space-x-8 px-4 md:px-6 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  <tab.icon className="h-4 w-4 mr-2 flex-shrink-0" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4 md:p-6">
            {/* Company Info Tab */}
            {activeTab === 'company' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Building2 className="h-5 w-5 mr-2 text-blue-600 flex-shrink-0" />
                    Informații Firmă
                  </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nume Firmă *
                    </label>
                    <input
                      type="text"
                      value={companyInfo.name}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="SC Exemplu SRL"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cod Fiscal *
                    </label>
                    <input
                      type="text"
                      value={companyInfo.fiscalCode}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, fiscalCode: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="RO12345678"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reg. Comerț *
                    </label>
                    <input
                      type="text"
                      value={companyInfo.commercialRegister}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, commercialRegister: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="J40/1234/2024"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Telefon
                    </label>
                    <input
                      type="text"
                      value={companyInfo.phone}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+40 21 123 4567"
                    />
                  </div>
                </div>

                {/* Address Section */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
                    Adresă
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Stradă</label>
                      <input
                        type="text"
                        value={companyInfo.street}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, street: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Str. Exemplu"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Număr</label>
                      <input
                        type="text"
                        value={companyInfo.number}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, number: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="123"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Bloc</label>
                      <input
                        type="text"
                        value={companyInfo.block}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, block: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="A1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Scară</label>
                      <input
                        type="text"
                        value={companyInfo.staircase}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, staircase: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Etaj</label>
                      <input
                        type="text"
                        value={companyInfo.floor}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, floor: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="2"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Apartament</label>
                      <input
                        type="text"
                        value={companyInfo.apartment}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, apartment: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="12"
                      />
                    </div>

                    <div className="md:col-span-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Județ</label>
                      <input
                        type="text"
                        value={companyInfo.county}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, county: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="București"
                      />
                    </div>
                  </div>
                </div>

                {/* Representative Section */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <User className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" />
                    Reprezentant Legal
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nume Reprezentant
                      </label>
                      <input
                        type="text"
                        value={companyInfo.representativeName}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, representativeName: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Popescu Ion"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Funcție
                      </label>
                      <input
                        type="text"
                        value={companyInfo.representativePosition}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, representativePosition: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Director General"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Reprezentant
                      </label>
                      <input
                        type="email"
                        value={companyInfo.representativeEmail}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, representativeEmail: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="director@firma.ro"
                      />
                    </div>
                  </div>
                </div>

                {/* Banking Section */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <svg className="h-4 w-4 mr-2 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    Informații Bancare
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        IBAN
                      </label>
                      <input
                        type="text"
                        value={companyInfo.iban}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, iban: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="RO49AAAA1B31007593840000"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bancă
                      </label>
                      <input
                        type="text"
                        value={companyInfo.bank}
                        onChange={(e) => setCompanyInfo({ ...companyInfo, bank: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Banca Transilvania"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    onClick={saveCompanyInfo}
                    disabled={loading}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Se salvează...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Salvează
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Email Settings Tab */}
            {activeTab === 'email' && (
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                  <Mail className="mr-2 h-6 w-6 text-orange-600" />
                  Configurare SMTP
                </h2>

                {message.text && (
                  <div className={`p-4 mb-4 rounded-lg flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                    }`}>
                    {message.type === 'success' ? <CheckCircle className="h-5 w-5 mr-2" /> : <AlertCircle className="h-5 w-5 mr-2" />}
                    {message.text}
                  </div>
                )}

                <form onSubmit={handleSaveEmailSettings} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Server SMTP */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Server SMTP</label>
                      <input
                        type="text"
                        name="smtpServer"
                        value={emailSettings.smtpServer}
                        onChange={handleEmailChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="ex: smtp.gmail.com"
                      />
                    </div>

                    {/* Port */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
                      <input
                        type="number"
                        name="smtpPort"
                        value={emailSettings.smtpPort}
                        onChange={handleEmailChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    {/* Username */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Utilizator SMTP</label>
                      <input
                        type="text"
                        name="smtpUsername"
                        value={emailSettings.smtpUsername}
                        onChange={handleEmailChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    {/* Password */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Parolă SMTP</label>
                      <input
                        type="password"
                        name="smtpPassword"
                        value={emailSettings.smtpPassword}
                        onChange={handleEmailChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        placeholder="••••••••"
                      />
                    </div>

                    {/* From Email */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Expeditor</label>
                      <input
                        type="email"
                        name="fromEmail"
                        value={emailSettings.fromEmail}
                        onChange={handleEmailChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>

                    {/* From Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Nume Expeditor</label>
                      <input
                        type="text"
                        name="fromName"
                        value={emailSettings.fromName}
                        onChange={handleEmailChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="enableSsl"
                      id="enableSsl"
                      checked={emailSettings.enableSsl}
                      onChange={handleEmailChange}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="enableSsl" className="ml-2 block text-sm text-gray-900">
                      Activează SSL/TLS
                    </label>
                  </div>

                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex items-center px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="animate-spin h-5 w-5 mr-2" />
                          Se salvează...
                        </>
                      ) : (
                        <>
                          <Save className="h-5 w-5 mr-2" />
                          Salvează Configurația
                        </>
                      )}
                    </button>
                  </div>
                </form>

                <div className="pt-6 border-t border-gray-100 mt-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Testare Conexiune</h3>
                  <p className="text-sm text-gray-600 mb-4">Verificați dacă setările introduse sunt corecte.</p>

                  {/* Butonul de Test */}
                  <button
                    type="button"
                    onClick={handleTestConnection}
                    disabled={testing || saving}
                    className="flex items-center px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                  >
                    {testing ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5 mr-2" />
                        Se testează...
                      </>
                    ) : (
                      <>
                        <TestTube className="h-5 w-5 mr-2" />
                        Rulează Testul SMTP
                      </>
                    )}
                  </button>

                  {/* Afișarea Rezultatului */}
                  {testResult && (
                    <div className={`mt-4 p-4 rounded-lg flex items-center ${testResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                      }`}>
                      {testResult.success ? <CheckCircle className="h-5 w-5 mr-2" /> : <AlertCircle className="h-5 w-5 mr-2" />}
                      {testResult.message}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* System Settings Tab */}
            {activeTab === 'system' && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                    <Settings className="h-5 w-5 mr-2 text-blue-600" />
                    Setări Sistem
                  </h2>
                  <p className="text-gray-600 mb-6">
                    Configurați comportamentul sistemului și valorile implicite.
                  </p>
                </div>

                {/* System settings content - keep existing implementation */}
                <div className="text-center py-8 text-gray-500">
                  Secțiunea de setări sistem va fi implementată în curând
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Additional Info Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">Informații Firmă</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Configurați datele companiei care vor apărea în contracte și comunicări oficiale.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center mb-4">
              <div className="bg-orange-100 p-2 rounded-lg">
                <Mail className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">Email SMTP</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Setați configurația pentru trimiterea automată de email-uri către clienți.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 p-2 rounded-lg">
                <Settings className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">Sistem</h3>
            </div>
            <p className="text-gray-600 text-sm">
              Personalizați comportamentul sistemului și valorile implicite pentru contracte.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;